import React, { useRef, useState } from 'react';
import { X, Download, Printer, Loader2 } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import toast from 'react-hot-toast';
import InitialBalanceReceiptTemplate from './InitialBalanceReceiptTemplate';

const InitialBalanceReceiptModal = ({ data, onClose }) => {
  const printRef = useRef(null);
  const [generating, setGenerating] = useState(false);

  // Imprimir diretamente via navegador usando CSS paged media (zero corte de texto)
  const handlePrintWindow = () => {
    if (!printRef.current) return;
    const printContent = printRef.current.innerHTML;
    const printWindow = window.open('', '_blank', 'width=950,height=900');
    if (!printWindow) {
      alert('Por favor, permita pop-ups para imprimir o recibo.');
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="pt-BR">
      <head>
        <meta charset="UTF-8">
        <title>Recibo Financeiro — Santiago Inhapim Planejados</title>
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap">
        <style>
          @page {
            size: A4 portrait;
            margin: 10mm 10mm 12mm 10mm;
          }
          * {
            box-sizing: border-box;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          body {
            margin: 0;
            padding: 0;
            background: #fff;
            font-family: 'Inter', system-ui, -apple-system, sans-serif;
            color: #243224;
          }
          .receipt-print-wrapper {
            width: 100% !important;
            min-height: auto !important;
            padding: 0 !important;
            background: #fff !important;
          }
          /* NUNCA quebrar linhas de tabelas ao meio */
          table {
            page-break-inside: auto !important;
            break-inside: auto !important;
          }
          tr {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          thead {
            display: table-header-group !important;
          }
          tfoot {
            display: table-footer-group !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          .receipt-section-card {
            page-break-inside: auto !important;
            break-inside: auto !important;
            margin-bottom: 4mm !important;
          }
          .receipt-header-grid,
          .receipt-summary-box {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
        </style>
      </head>
      <body>
        ${printContent}
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.focus();
              window.print();
              window.close();
            }, 350);
          };
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleGeneratePdf = async () => {
    if (!printRef.current) return;
    setGenerating(true);
    try {
      const rootEl = printRef.current;
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const marginMm = 10;
      const contentWidthMm = pdfWidth - (marginMm * 2);
      const maxPageHeightMm = pdfHeight - (marginMm * 2);

      // Coletar todos os blocos principais para paginação física DOM real
      // 1. Cabeçalho + Resumo
      // 2. Cada card de tabela (receipt-section-card)
      // 3. Rodapé
      const headerGrid = rootEl.querySelector('.receipt-header-grid');
      const summaryBox = rootEl.querySelector('.receipt-summary-box');
      const sectionCards = Array.from(rootEl.querySelectorAll('.receipt-section-card'));
      const footerEl = rootEl.querySelector('div[style*="border-top"]:last-child') || rootEl.lastElementChild;

      // Montar fila de blocos a serem impressos
      // Para cada seção de tabela longa, se tiver muitas linhas, quebramos em pedaços de até 8 linhas para nunca estourar a página A4
      const renderBlocks = [];

      if (headerGrid) renderBlocks.push(headerGrid);
      if (summaryBox) renderBlocks.push(summaryBox);

      sectionCards.forEach(card => {
        const table = card.querySelector('table');
        const rows = table ? Array.from(table.querySelectorAll('tbody tr')) : [];

        // Se a tabela for grande (mais de 7 linhas), paginar os blocos de linhas
        if (rows.length > 7) {
          const headerTitle = card.querySelector('div[style*="font-size: 11px"]') || card.firstElementChild;
          const tableHeader = table.querySelector('thead');
          const chunkSize = 7;

          for (let i = 0; i < rows.length; i += chunkSize) {
            const chunkRows = rows.slice(i, i + chunkSize);
            const chunkCard = document.createElement('div');
            chunkCard.style.cssText = card.style.cssText;
            chunkCard.style.marginBottom = '4mm';

            if (headerTitle) {
              const titleClone = headerTitle.cloneNode(true);
              if (i > 0) titleClone.textContent += ' (continuação)';
              chunkCard.appendChild(titleClone);
            }

            const chunkTable = document.createElement('table');
            chunkTable.style.cssText = table.style.cssText;
            if (tableHeader) chunkTable.appendChild(tableHeader.cloneNode(true));

            const chunkTbody = document.createElement('tbody');
            chunkRows.forEach(r => chunkTbody.appendChild(r.cloneNode(true)));
            chunkTable.appendChild(chunkTbody);

            // Adiciona tfoot se for o último pedaço
            if (i + chunkSize >= rows.length && table.querySelector('tfoot')) {
              chunkTable.appendChild(table.querySelector('tfoot').cloneNode(true));
            }

            chunkCard.appendChild(chunkTable);
            renderBlocks.push(chunkCard);
          }
        } else {
          renderBlocks.push(card);
        }
      });

      if (footerEl && footerEl !== summaryBox) {
        renderBlocks.push(footerEl);
      }

      // Renderizar blocos respeitando o limite físico da folha
      let currentYMm = marginMm;
      let pageNum = 0;

      for (let i = 0; i < renderBlocks.length; i++) {
        const block = renderBlocks[i];
        const isTransient = !rootEl.contains(block);
        if (isTransient) {
          block.style.position = 'fixed';
          block.style.left = '-9999px';
          block.style.width = `${rootEl.offsetWidth}px`;
          document.body.appendChild(block);
        }

        const blockCanvas = await html2canvas(block, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff'
        });

        if (isTransient) {
          document.body.removeChild(block);
        }

        const blockRatio = blockCanvas.width / blockCanvas.height;
        const blockHeightMm = contentWidthMm / blockRatio;

        // Se o bloco não couber na folha atual, cria nova página
        if (currentYMm + blockHeightMm > maxPageHeightMm + marginMm && pageNum > 0) {
          pdf.addPage();
          currentYMm = marginMm;
        }

        const imgData = blockCanvas.toDataURL('image/jpeg', 0.95);
        pdf.addImage(imgData, 'JPEG', marginMm, currentYMm, contentWidthMm, blockHeightMm);
        currentYMm += blockHeightMm + 2; // Espaçamento de 2mm entre blocos
        pageNum++;
      }

      const projectNumber = data?.project?.number || data?.balance?.id || 'recibo';
      const clientName = data?.clientName || data?.balance?.client_name || 'cliente';
      const cleanClientName = clientName.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
      const generatedDate = data?.project?.generatedAt
        ? new Date(data.project.generatedAt).toLocaleDateString('pt-BR').replace(/\//g, '-')
        : new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
      const fileName = `${cleanClientName}-${projectNumber}-${generatedDate}.pdf`;
      pdf.save(fileName);
      toast?.success?.('PDF baixado com sucesso!') || alert('PDF baixado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      alert('Erro ao gerar PDF: ' + error.message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.6)' }}
    >
      <div
        className="relative bg-white rounded-xl shadow-2xl overflow-hidden"
        style={{ width: '940px', maxWidth: '95vw', maxHeight: '95vh', display: 'flex', flexDirection: 'column' }}
      >
        <div
          className="flex items-center justify-between px-6 py-4 border-b border-gray-200"
          style={{ background: 'linear-gradient(135deg, #48984f, #3d7d43)', color: 'white', flexShrink: 0 }}
        >
          <h2 className="text-lg font-bold">Prévia do Recibo — PDF Premium</h2>
          <div className="flex items-center gap-3">
            <button
              onClick={handlePrintWindow}
              className="flex items-center gap-2 px-3 py-2 bg-emerald-700 text-white rounded-lg font-semibold text-sm hover:bg-emerald-800 transition-colors shadow-sm"
              title="Imprimir com quebra de página automática do navegador (recomendado para impressão física)"
            >
              <Printer size={16} />
              Imprimir Direto
            </button>
            <button
              onClick={handleGeneratePdf}
              disabled={generating}
              className="flex items-center gap-2 px-4 py-2 bg-white text-[#48984f] rounded-lg font-semibold text-sm hover:bg-emerald-50 disabled:opacity-60 shadow-sm"
            >
              {generating ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Gerando...
                </>
              ) : (
                <>
                  <Download size={16} />
                  Baixar PDF
                </>
              )}
            </button>
            <button
              onClick={onClose}
              className="p-1 hover:bg-emerald-700 rounded"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div
          className="flex-1 overflow-auto p-4"
          style={{ background: '#d1d5db', display: 'flex', justifyContent: 'center' }}
        >
          <div style={{ display: 'inline-block', minWidth: '210mm' }}>
            <div ref={printRef}>
              <InitialBalanceReceiptTemplate data={data} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InitialBalanceReceiptModal;